export interface LoanFile {
  id: string
  borrowerName: string
  loanNumber: string
  loanAmount: number
  loanType: 'Conventional' | 'FHA' | 'VA' | 'Jumbo'
  stage:
    | 'Processing'
    | 'Underwriting'
    | 'CD Sent'
    | 'Clear to Close'
    | 'Closed'
    | 'At Risk'
  daysInStage: number
  lastTouched: string
  closingDate?: string
  approvalToClearDays?: number
  metrics: string[]
  pointsByMetric: Record<string, number>
  isAtRisk?: boolean
  notTouched?: boolean
}

export const mockData = {
  user: {
    name: 'John Davis',
    role: 'LOAN PROCESSOR',
    month: 'May 2025',
    date: 'May 14, 2025',
  },
  today: {
    points: '1,450',
    remaining: '350',
    daysLeft: '17',
    dailyTarget: '1,380',
  },
  monthly: {
    points: '7,000',
    goal: '10,000',
    remaining: '3,000',
    percentComplete: 70,
    projected: '9,700',
  },
  performance: [
    {
      id: 'conditions',
      title: 'CONDITIONS CLEARED',
      subtitle: 'UNDER 3 DAYS (CORE DRIVER)',
      color: 'bg-[#16A34A]',
      textColor: 'text-[#16A34A]',
      points: '7,500',
      count: '15',
      rate: '500 PTS / FILE',
      maxPoints: '12,500',
      maxCount: '~25 Files',
      icon: 'ClipboardCheckIcon',
    },
    {
      id: 'cd',
      title: 'CD SENT',
      subtitle: 'UNDER 3 DAYS FROM APPROVAL',
      color: 'bg-[#7C3AED]',
      textColor: 'text-[#7C3AED]',
      points: '1,750',
      count: '7',
      rate: '250 PTS / FILE',
      maxPoints: '3,750',
      maxCount: '~15 Files',
      icon: 'MailIcon',
    },
    {
      id: 'speed',
      title: 'APPROVAL -> CLEAR',
      subtitle: 'TO CLOSE SPEED BONUS',
      color: 'bg-[#E8801F]',
      textColor: 'text-[#E8801F]',
      points: '800',
      count: '4',
      rate: '≤ 14 DAYS = 400 PTS\n≤ 21 DAYS = 100 PTS',
      maxPoints: '3,500',
      maxCount: '~15 Fast Files',
      icon: 'TimerIcon',
      isSpeed: true,
    },
    {
      id: 'closed',
      title: 'CLOSED',
      subtitle: '(LOWEST WEIGHT)',
      color: 'bg-[#2563EB]',
      textColor: 'text-[#2563EB]',
      points: '500',
      count: '5',
      rate: '100 PTS / FILE',
      maxPoints: '1,500',
      maxCount: '~15 Files',
      icon: 'CheckCircle2Icon',
    },
    {
      id: 'penalty',
      title: 'FILES NOT MOVING',
      subtitle: '(PENALTY)',
      color: 'bg-[#DC2626]',
      textColor: 'text-[#DC2626]',
      points: '-250',
      count: '2',
      rate: 'SEE PENALTY RULES BELOW',
      maxPoints: '0',
      maxCount: '0',
      icon: 'AlertTriangleIcon',
      isPenalty: true,
    },
  ],
  advancement: {
    tenure: { current: 16, required: 18 },
    performance: { current: 72, required: 90 },
    currentTier: '20 - 24 LOANS',
    tiers: [
      { range: '1 - 9 LOANS', incentive: '$0', sub: '(NO INCENTIVE)' },
      { range: '10 - 14 LOANS', incentive: '$15', sub: 'PER UNIT' },
      { range: '15 - 19 LOANS', incentive: '$25', sub: 'PER UNIT' },
      {
        range: '20 - 24 LOANS',
        incentive: '$35',
        sub: 'PER UNIT',
        isCurrent: true,
      },
      { range: '25+ LOANS', incentive: '$45', sub: 'PER UNIT' },
    ],
  },
}

// 30 active pipeline files with realistic distribution
export const loanFiles: LoanFile[] = [
  // 5 CLOSED files (also contributed conditions + cd earlier)
  {
    id: 'f001',
    borrowerName: 'Maria Hernandez',
    loanNumber: 'MD-2025-1847',
    loanAmount: 425000,
    loanType: 'Conventional',
    stage: 'Closed',
    daysInStage: 0,
    lastTouched: 'Today',
    closingDate: 'May 12, 2025',
    approvalToClearDays: 12,
    metrics: ['conditions', 'cd', 'speed', 'closed'],
    pointsByMetric: { conditions: 500, cd: 250, speed: 400, closed: 100 },
  },
  {
    id: 'f002',
    borrowerName: "James O'Brien",
    loanNumber: 'MD-2025-1823',
    loanAmount: 615000,
    loanType: 'Jumbo',
    stage: 'Closed',
    daysInStage: 1,
    lastTouched: 'Yesterday',
    closingDate: 'May 9, 2025',
    approvalToClearDays: 13,
    metrics: ['conditions', 'cd', 'speed', 'closed'],
    pointsByMetric: { conditions: 500, cd: 250, speed: 400, closed: 100 },
  },
  {
    id: 'f003',
    borrowerName: 'Priya Patel',
    loanNumber: 'MD-2025-1801',
    loanAmount: 295000,
    loanType: 'FHA',
    stage: 'Closed',
    daysInStage: 3,
    lastTouched: '3 days ago',
    closingDate: 'May 6, 2025',
    approvalToClearDays: 19,
    metrics: ['conditions', 'cd', 'closed'],
    pointsByMetric: { conditions: 500, cd: 250, closed: 100 },
  },
  {
    id: 'f004',
    borrowerName: 'Robert Chen',
    loanNumber: 'MD-2025-1789',
    loanAmount: 510000,
    loanType: 'Conventional',
    stage: 'Closed',
    daysInStage: 5,
    lastTouched: '5 days ago',
    closingDate: 'May 4, 2025',
    approvalToClearDays: 22,
    metrics: ['conditions', 'cd', 'closed'],
    pointsByMetric: { conditions: 500, cd: 250, closed: 100 },
  },
  {
    id: 'f005',
    borrowerName: 'Aisha Williams',
    loanNumber: 'MD-2025-1772',
    loanAmount: 348000,
    loanType: 'VA',
    stage: 'Closed',
    daysInStage: 7,
    lastTouched: '1 week ago',
    closingDate: 'May 2, 2025',
    approvalToClearDays: 16,
    metrics: ['conditions', 'closed'],
    pointsByMetric: { conditions: 500, closed: 100 },
  },
  // 4 CLEAR TO CLOSE (still in pipeline, some contributed speed bonus + cd)
  {
    id: 'f006',
    borrowerName: 'David Kim',
    loanNumber: 'MD-2025-1902',
    loanAmount: 472000,
    loanType: 'Conventional',
    stage: 'Clear to Close',
    daysInStage: 2,
    lastTouched: 'Today',
    approvalToClearDays: 20,
    metrics: ['conditions', 'cd', 'speed'],
    pointsByMetric: { conditions: 500, cd: 250, speed: 0 },
  },
  {
    id: 'f007',
    borrowerName: 'Sofia Ramirez',
    loanNumber: 'MD-2025-1891',
    loanAmount: 388000,
    loanType: 'FHA',
    stage: 'Clear to Close',
    daysInStage: 1,
    lastTouched: 'Today',
    approvalToClearDays: 21,
    metrics: ['conditions', 'cd', 'speed'],
    pointsByMetric: { conditions: 500, cd: 250, speed: 0 },
  },
  {
    id: 'f008',
    borrowerName: 'Michael Thompson',
    loanNumber: 'MD-2025-1885',
    loanAmount: 550000,
    loanType: 'Jumbo',
    stage: 'Clear to Close',
    daysInStage: 4,
    lastTouched: '2 days ago',
    metrics: ['conditions', 'cd'],
    pointsByMetric: { conditions: 500, cd: 250 },
  },
  {
    id: 'f009',
    borrowerName: 'Linda Garcia',
    loanNumber: 'MD-2025-1879',
    loanAmount: 312000,
    loanType: 'Conventional',
    stage: 'Clear to Close',
    daysInStage: 2,
    lastTouched: 'Today',
    metrics: ['conditions', 'cd'],
    pointsByMetric: { conditions: 500, cd: 250 },
  },
  // CD SENT stage (couple more conditions-cleared files)
  {
    id: 'f010',
    borrowerName: 'Brandon Walker',
    loanNumber: 'MD-2025-1912',
    loanAmount: 428000,
    loanType: 'Conventional',
    stage: 'CD Sent',
    daysInStage: 2,
    lastTouched: 'Today',
    metrics: ['conditions', 'cd'],
    pointsByMetric: { conditions: 500, cd: 250 },
  },
  {
    id: 'f011',
    borrowerName: 'Emily Johnson',
    loanNumber: 'MD-2025-1908',
    loanAmount: 365000,
    loanType: 'FHA',
    stage: 'CD Sent',
    daysInStage: 1,
    lastTouched: 'Today',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  // 10 more UNDERWRITING / PROCESSING with conditions cleared
  {
    id: 'f012',
    borrowerName: 'Carlos Mendoza',
    loanNumber: 'MD-2025-1923',
    loanAmount: 495000,
    loanType: 'Conventional',
    stage: 'Underwriting',
    daysInStage: 3,
    lastTouched: 'Yesterday',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f013',
    borrowerName: 'Hannah Lee',
    loanNumber: 'MD-2025-1931',
    loanAmount: 278000,
    loanType: 'FHA',
    stage: 'Underwriting',
    daysInStage: 2,
    lastTouched: 'Today',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f014',
    borrowerName: 'Tyrone Jackson',
    loanNumber: 'MD-2025-1938',
    loanAmount: 412000,
    loanType: 'VA',
    stage: 'Underwriting',
    daysInStage: 1,
    lastTouched: 'Today',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f015',
    borrowerName: 'Sarah Mitchell',
    loanNumber: 'MD-2025-1942',
    loanAmount: 338000,
    loanType: 'Conventional',
    stage: 'Underwriting',
    daysInStage: 4,
    lastTouched: '2 days ago',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f016',
    borrowerName: 'Jin Park',
    loanNumber: 'MD-2025-1949',
    loanAmount: 605000,
    loanType: 'Jumbo',
    stage: 'Underwriting',
    daysInStage: 2,
    lastTouched: 'Today',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f017',
    borrowerName: 'Olivia Brown',
    loanNumber: 'MD-2025-1955',
    loanAmount: 285000,
    loanType: 'FHA',
    stage: 'Underwriting',
    daysInStage: 1,
    lastTouched: 'Today',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f018',
    borrowerName: 'Anthony Russo',
    loanNumber: 'MD-2025-1962',
    loanAmount: 528000,
    loanType: 'Conventional',
    stage: 'Underwriting',
    daysInStage: 3,
    lastTouched: 'Yesterday',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f019',
    borrowerName: 'Grace Nguyen',
    loanNumber: 'MD-2025-1968',
    loanAmount: 392000,
    loanType: 'Conventional',
    stage: 'Processing',
    daysInStage: 2,
    lastTouched: 'Today',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  {
    id: 'f020',
    borrowerName: 'Marcus Davis',
    loanNumber: 'MD-2025-1974',
    loanAmount: 445000,
    loanType: 'Conventional',
    stage: 'Processing',
    daysInStage: 1,
    lastTouched: 'Today',
    metrics: ['conditions'],
    pointsByMetric: { conditions: 500 },
  },
  // AT RISK / Penalty files
  {
    id: 'f021',
    borrowerName: 'Kevin Anderson',
    loanNumber: 'MD-2025-1812',
    loanAmount: 358000,
    loanType: 'Conventional',
    stage: 'At Risk',
    daysInStage: 8,
    lastTouched: '4 days ago',
    metrics: ['penalty'],
    pointsByMetric: { penalty: -150 },
    isAtRisk: true,
    notTouched: true,
  },
  {
    id: 'f022',
    borrowerName: 'Rachel Foster',
    loanNumber: 'MD-2025-1798',
    loanAmount: 422000,
    loanType: 'FHA',
    stage: 'At Risk',
    daysInStage: 6,
    lastTouched: '5 days ago',
    metrics: ['penalty'],
    pointsByMetric: { penalty: -100 },
    isAtRisk: true,
    notTouched: true,
  },
  // Active pipeline (no points yet this month)
  {
    id: 'f023',
    borrowerName: 'Daniel Wright',
    loanNumber: 'MD-2025-1981',
    loanAmount: 372000,
    loanType: 'Conventional',
    stage: 'Processing',
    daysInStage: 1,
    lastTouched: 'Today',
    metrics: [],
    pointsByMetric: {},
  },
  {
    id: 'f024',
    borrowerName: 'Isabella Cruz',
    loanNumber: 'MD-2025-1986',
    loanAmount: 318000,
    loanType: 'FHA',
    stage: 'Processing',
    daysInStage: 2,
    lastTouched: 'Today',
    metrics: [],
    pointsByMetric: {},
  },
  {
    id: 'f025',
    borrowerName: 'Steven Bell',
    loanNumber: 'MD-2025-1991',
    loanAmount: 567000,
    loanType: 'Jumbo',
    stage: 'Processing',
    daysInStage: 3,
    lastTouched: 'Yesterday',
    metrics: [],
    pointsByMetric: {},
  },
  {
    id: 'f026',
    borrowerName: 'Natalie Kim',
    loanNumber: 'MD-2025-1996',
    loanAmount: 295000,
    loanType: 'VA',
    stage: 'Processing',
    daysInStage: 1,
    lastTouched: 'Today',
    metrics: [],
    pointsByMetric: {},
  },
  {
    id: 'f027',
    borrowerName: 'George Sullivan',
    loanNumber: 'MD-2025-2001',
    loanAmount: 488000,
    loanType: 'Conventional',
    stage: 'Underwriting',
    daysInStage: 4,
    lastTouched: 'Today',
    metrics: [],
    pointsByMetric: {},
  },
  {
    id: 'f028',
    borrowerName: 'Yara Hassan',
    loanNumber: 'MD-2025-2008',
    loanAmount: 412000,
    loanType: 'Conventional',
    stage: 'Underwriting',
    daysInStage: 2,
    lastTouched: 'Today',
    metrics: [],
    pointsByMetric: {},
  },
  {
    id: 'f029',
    borrowerName: 'Patrick Owens',
    loanNumber: 'MD-2025-2015',
    loanAmount: 638000,
    loanType: 'Jumbo',
    stage: 'Underwriting',
    daysInStage: 3,
    lastTouched: 'Yesterday',
    metrics: [],
    pointsByMetric: {},
  },
  {
    id: 'f030',
    borrowerName: 'Chloe Martinez',
    loanNumber: 'MD-2025-2022',
    loanAmount: 345000,
    loanType: 'FHA',
    stage: 'Processing',
    daysInStage: 1,
    lastTouched: 'Today',
    metrics: [],
    pointsByMetric: {},
  },
]

export const stageColors: Record<string, string> = {
  Processing: 'bg-gray-100 text-gray-700 border-gray-200',
  Underwriting: 'bg-purple-50 text-purple-700 border-purple-200',
  'CD Sent': 'bg-blue-50 text-blue-700 border-blue-200',
  'Clear to Close': 'bg-amber-50 text-amber-700 border-amber-200',
  Closed: 'bg-green-50 text-green-700 border-green-200',
  'At Risk': 'bg-red-50 text-red-700 border-red-200',
}
